const LOW_PRIORITY_IMAGE_RE =
  /\b(cgi|render|renders|floor[\s_-]?plan|floorplan|payment|brochure|master[\s_-]?plan|site[\s_-]?plan|map|qr|logo|whatsapp)\b/i;

function normalizedImageUrl(url: string): string {
  try {
    const parsed = new URL(url, "https://hivehomes.local");
    return decodeURIComponent(parsed.searchParams.get("u") || url).toLowerCase();
  } catch {
    try {
      return decodeURIComponent(url).toLowerCase();
    } catch {
      return url.toLowerCase();
    }
  }
}

function imagePriority(url: string, listingId?: string): number {
  const normalized = normalizedImageUrl(url);
  const filename = normalized.split(/[/?#]/).pop() ?? normalized;
  let score = 0;

  if (LOW_PRIORITY_IMAGE_RE.test(normalized)) score += 100;

  const embeddedListingId = filename.match(/property[_-](\d+)/)?.[1];
  if (listingId && embeddedListingId && embeddedListingId !== String(listingId)) {
    score += 10;
  }

  return score;
}

interface RankPropertyImagesOptions {
  coverOffset?: number;
}

export function rankPropertyImages(
  images: readonly (string | null | undefined)[] | undefined,
  listingId?: string,
  options: RankPropertyImagesOptions = {},
): string[] {
  const seen = new Set<string>();

  const ranked = (images ?? [])
    .map((image, index) => ({ image: image?.trim(), index }))
    .filter((entry): entry is { image: string; index: number } => Boolean(entry.image))
    .filter(({ image }) => {
      const key = normalizedImageUrl(image);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(({ image, index }) => ({
      image,
      index,
      score: imagePriority(image, listingId),
    }))
    .sort((a, b) => a.score - b.score || a.index - b.index);

  const coverOffset = options.coverOffset ?? 0;
  if (coverOffset > 0 && ranked.length > 1) {
    const bestScore = ranked[0]?.score;
    const preferred = ranked.filter((entry) => entry.score === bestScore);

    if (preferred.length > 1) {
      const offset = coverOffset % preferred.length;
      const rotated = [
        ...preferred.slice(offset),
        ...preferred.slice(0, offset),
        ...ranked.filter((entry) => entry.score !== bestScore),
      ];

      return rotated.map(({ image }) => image);
    }
  }

  return ranked
    .map(({ image }) => image);
}

export function getPropertyCoverOffsetMap(
  properties: readonly {
    id: string;
    images?: readonly (string | null | undefined)[];
  }[],
): Map<string, number> {
  const coverCounts = new Map<string, number>();
  const offsets = new Map<string, number>();

  for (const property of properties) {
    const cover = rankPropertyImages(property.images, property.id)[0];
    if (!cover) {
      offsets.set(property.id, 0);
      continue;
    }

    const key = normalizedImageUrl(cover);
    const offset = coverCounts.get(key) ?? 0;
    coverCounts.set(key, offset + 1);
    offsets.set(property.id, offset);
  }

  return offsets;
}
